import React from "react";
import NormalText from "./NormalText";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { ClinicContext, SessionContext } from "contexts";
import { FormNameType, ProcedureType, UploadTypeType } from "api/type";
import { submitAdvertiseForm, TSubmitAdvertiseFormRequest } from "api/clinicos";
import { generateUUID } from "utils/generateUUID";
import PrimaryButton from "./PrimaryButton";
import { isAISupportedProcedure } from "utils/isAISuportedProcedureAndArea";
import { mixpanelTrack } from "utils/mixpanel";
import { useUserLocation } from "utils/customHook";

type ModelContentType = {
  batchId: string | null;
  selectedProcedure: ProcedureType | null;
  handleCloseModal: () => void;
  handleSetErrorMessage: (message: string) => void;
  handleSetIsFormSubmittedTrue: (value: boolean) => void;
  formName: FormNameType;
  uploadType: UploadTypeType;
};

const ModalContent: React.FC<ModelContentType> = ({
  batchId,
  selectedProcedure,
  handleCloseModal,
  handleSetErrorMessage,
  handleSetIsFormSubmittedTrue,
  formName,
  uploadType,
}) => {
  // Contexts
  const session = React.useContext(SessionContext);
  const clinic = React.useContext(ClinicContext);

  // Custom hooks
  const fetchUserLocation = useUserLocation(session);

  // States
  const [name, setName] = React.useState<string>("");
  const [phone, setPhone] = React.useState<string>("");
  const [loading, setLoading] = React.useState<boolean>(false);
  const [modalError, setModalError] = React.useState<string | null>(null);

  const handleViewResults = async () => {
    if (!session) return;
    setModalError(null);

    if (name.length < 3) {
      setModalError("Name must be at least 3 characters.");
      return;
    }
    if (!phone || phone.length < 10) {
      setModalError("Please enter a valid phone number.");
      return;
    }
    const firstName = name.split(" ")[0];
    const lastName = name.split(" ").slice(1).join(" ");

    setLoading(true);
    try {
      const body: TSubmitAdvertiseFormRequest = {
        ...(batchId ? { batch_id: batchId } : {}),
        form_user_id: generateUUID(),
        form_name: formName,
        is_debug: process.env.REACT_APP_ENV === "production" ? false : true,
        form_data: { name: name, phone: phone },
        patient_record: {
          first_name: firstName,
          last_name: lastName,
          phone: phone,
          location: await fetchUserLocation(),
          upload_type: uploadType,
          ...(selectedProcedure
            ? { procedures_of_interest: [selectedProcedure] }
            : {}),
        },
      };
      await submitAdvertiseForm(session, body);

      mixpanelTrack("User Details Captured", [
        {
          propertyName: "Clinic Name",
          propertyValue: clinic?.clinic_name ?? "failed to get clinic name",
        },
        {
          propertyName: "Name",
          propertyValue: name ?? "Fail to capture name",
        },
        {
          propertyName: "Phone",
          propertyValue: phone ?? "Fail to capture phone",
        },
        {
          propertyName: "Note",
          propertyValue:
            "This event could be from the Advertise Form or Onboarding Form.",
        },
      ]);

      handleSetIsFormSubmittedTrue(true);
    } catch (error) {
      if (error instanceof Error) {
        if (error.message.includes("401"))
          handleSetErrorMessage(
            "Session has expired. Page will refresh in 3 seconds.",
          );
      } else {
        handleSetErrorMessage("An unexpected error occurred");
      }
    } finally {
      setLoading(false);
      handleCloseModal();
    }
  };

  return (
    <div style={styles.container}>
      {selectedProcedure && isAISupportedProcedure(selectedProcedure) ? (
        <NormalText textContent="Enter details to continue" />
      ) : (
        <NormalText textContent="Enter details to view the results" />
      )}
      <div>
        <div style={{ display: "flex" }}>
          <NormalText textContent="Name" />
          <NormalText textContent="*" style={{ color: "red" }} />
        </div>
        <input
          type="text"
          onChange={(e) => setName(e.target.value)}
          required
          autoFocus
          style={{ fontSize: "16px", padding: "0 10px", ...styles.input }}
        />
      </div>
      <div>
        <div style={{ display: "flex" }}>
          <NormalText textContent="Phone" />
          <NormalText textContent="*" style={{ color: "red" }} />
        </div>
        <PhoneInput
          containerStyle={{ fontSize: "16px" }}
          inputStyle={styles.input}
          country={"us"}
          onChange={setPhone}
          inputProps={{
            required: true,
          }}
        />
      </div>
      {modalError && (
        <NormalText textContent={modalError} style={styles.errorText} />
      )}
      <PrimaryButton
        text={
          selectedProcedure && isAISupportedProcedure(selectedProcedure)
            ? "View Results"
            : "Submit"
        }
        type="button"
        fullWidth
        loading={loading}
        disabled={!name || !phone}
        displayPoweredBy={false}
        onClick={() => handleViewResults()}
      />
    </div>
  );
};

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  input: {
    fontWeight: 500,
    width: "100%",
    boxSizing: "border-box",
    border: "1px solid #CACACA",
    height: "35px",
    borderRadius: "3px",
  },
  errorText: {
    color: "red",
    fontSize: "14px",
  },
} as const;

export default ModalContent;
